import { useCallback, useMemo } from 'react';

import { type QueryKey, useMutation, useQuery } from '@tanstack/react-query';

import {
  type ValidatePromoCodeRequestOptions,
  validateCodeName,
  validateCreateOrUpdatePromoCodeRequest,
} from '@luxuryescapes/contract-svc-promo';

import useDebounced from '~/hooks/useDebounced';

import type { CheckPromoDraftResponse, CreateOrUpdatePromoPayload } from '~/services/PromoService';

export { validateCodeName, validateCreateOrUpdatePromoCodeRequest };

const VALIDATION_DELAY_MS = 400;

export interface PromoCodeAvailabilityCheck {
  /** Include the API/client scope; brand and names are appended by the hook. */
  queryKey: QueryKey;
  brand: string;
  codeNameExists: (codeName: string, brand: string, options?: { signal?: AbortSignal }) => Promise<boolean>;
}

export interface PromoCodeCheckOptions<ApiResult = CheckPromoDraftResponse> {
  value?: string;
  enabled?: boolean;
  multipleNames?: boolean;
  debounceMs?: number;
  /** Supply only when the caller needs a new, unused code name. */
  availability?: PromoCodeAvailabilityCheck;
  /** Override whole-promo validation; defaults to the contract validator. */
  validatePromo?: typeof validateCreateOrUpdatePromoCodeRequest;
  /** Optional server validation; the caller supplies its API and tenant context. */
  validatePromoInput?: (payload: CreateOrUpdatePromoPayload) => Promise<ApiResult>;
}

export const promoCodeCheckQueryKeys = {
  all: ['promo-code-check'] as const,
  availability: (scope: QueryKey, brand: string | undefined, names: ReadonlyArray<string>) =>
    [...promoCodeCheckQueryKeys.all, 'availability', scope, brand, names] as const,
};

export function usePromoCodeCheck<ApiResult = CheckPromoDraftResponse>({
  value = '',
  enabled = true,
  multipleNames = false,
  debounceMs = VALIDATION_DELAY_MS,
  availability,
  validatePromo: validateWholePromo = validateCreateOrUpdatePromoCodeRequest,
  validatePromoInput,
}: PromoCodeCheckOptions<ApiResult> = {}) {
  const debouncedName = useDebounced(value, debounceMs);
  const names = useMemo(
    () => (multipleNames ? debouncedName.split(',').map((name) => name.trim()) : [debouncedName]),
    [debouncedName, multipleNames],
  );
  const isDebouncing = value !== debouncedName;
  const formatValidation = useMemo(() => {
    if (!enabled || !debouncedName || isDebouncing) return null;
    const results = names.map(validateCodeName);
    const failure = results.flatMap((result) => result.validationResults).find((result) => !result.isValid);
    return { isValid: results.every((result) => result.isValid), message: failure?.fixSuggestion ?? 'Valid format' };
  }, [enabled, debouncedName, isDebouncing, names]);

  const availabilityCheck = useQuery({
    queryKey: promoCodeCheckQueryKeys.availability(availability?.queryKey ?? [], availability?.brand, names),
    queryFn: async ({ signal }) => {
      if (!availability) throw new Error('No promo code availability callback was supplied.');
      const results = await Promise.all(
        names.map(async (name) => ({
          name,
          exists: await availability.codeNameExists(name, availability.brand, { signal }),
        })),
      );
      const taken = results.filter((result) => result.exists).map((result) => result.name);
      return {
        isValid: taken.length === 0,
        message: taken.length ? `Already taken: ${taken.join(', ')}` : 'Name available',
      };
    },
    enabled: Boolean(availability?.brand) && formatValidation?.isValid === true,
    retry: false,
  });

  const promoValidation = useMutation({
    mutationFn: ({
      payload,
      options,
    }: {
      payload: CreateOrUpdatePromoPayload;
      options?: ValidatePromoCodeRequestOptions;
    }) =>
      validateWholePromo(payload, {
        ...(availability ? { codeNameExists: availability.codeNameExists } : {}),
        ...options,
      }),
    retry: false,
  });
  const inputValidation = useMutation({
    mutationFn: (payload: CreateOrUpdatePromoPayload) => {
      if (!validatePromoInput) throw new Error('No promo input validation API callback was supplied.');
      return validatePromoInput(payload);
    },
    retry: false,
  });
  const validatePromo = useCallback(
    (payload: CreateOrUpdatePromoPayload, options?: ValidatePromoCodeRequestOptions) =>
      promoValidation.mutateAsync({ payload, options }),
    [promoValidation.mutateAsync],
  );

  const isCheckingAvailability =
    Boolean(availability) && formatValidation?.isValid === true && availabilityCheck.isFetching;
  const validation =
    !formatValidation?.isValid || !availability
      ? formatValidation
      : availabilityCheck.isError
        ? { isValid: false, message: 'Unable to check name availability. Try again.' }
        : (availabilityCheck.data ?? null);

  return {
    validateCodeName,
    formatValidation,
    validation,
    isDebouncing,
    isCheckingAvailability,
    availabilityCheck,
    validatePromo,
    promoValidation,
    validatePromoInput: inputValidation.mutateAsync,
    canValidatePromoInput: Boolean(validatePromoInput),
    inputValidation,
  };
}
